import { MIN_PASSWORD_LENGTH } from "@repo/auth/client"
import { Button } from "@repo/ui/components/button"
import { Input } from "@repo/ui/components/input"
import { Spinner } from "@repo/ui/components/spinner"
import { LogOut } from "lucide-react"
import { type FormEvent, useEffect, useState } from "react"

import { PageHeader } from "@/components/page/page-header"
import {
  SaveBar,
  SettingsRow,
  SettingsSection,
} from "@/components/page/settings"
import { authClient, useSession } from "@/lib/auth-client"

function NameSection({ name }: { name: string }) {
  const [value, setValue] = useState(name)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = value.trim()
  const dirty = trimmed !== name

  async function save(event: FormEvent) {
    event.preventDefault()

    if (trimmed === "") {
      setError("Enter your name.")
      return
    }

    setSaving(true)
    setError(null)

    const { error: failure } = await authClient.updateUser({ name: trimmed })

    setSaving(false)

    if (failure) {
      setError(failure.message ?? "Could not save your name.")
      return
    }

    setSaved(true)
  }

  return (
    <form onSubmit={save}>
      <SettingsSection
        title="Your name"
        description="Shown in the menu and on anything you send from the dashboard."
        actions={
          <SaveBar
            dirty={dirty}
            saving={saving}
            saved={saved}
            error={error}
            onCancel={() => {
              setValue(name)
              setError(null)
            }}
          />
        }
      >
        <SettingsRow label="Name" htmlFor="profile-name">
          <Input
            id="profile-name"
            autoComplete="name"
            placeholder="Your name"
            maxLength={100}
            value={value}
            onChange={(event) => {
              setValue(event.target.value)
              setSaved(false)
            }}
          />
        </SettingsRow>
      </SettingsSection>
    </form>
  )
}

function PasswordSection() {
  const [current, setCurrent] = useState("")
  const [next, setNext] = useState("")
  const [confirm, setConfirm] = useState("")
  const [working, setWorking] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function change(event: FormEvent) {
    event.preventDefault()
    setDone(false)

    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }

    if (next !== confirm) {
      setError("The two new passwords are not the same.")
      return
    }

    setWorking(true)
    setError(null)

    const { error: failure } = await authClient.changePassword({
      currentPassword: current,
      newPassword: next,
      // Anywhere else you are signed in is signed out, in case it was not you.
      revokeOtherSessions: true,
    })

    setWorking(false)

    if (failure) {
      setError(failure.message ?? "Could not change the password.")
      return
    }

    setCurrent("")
    setNext("")
    setConfirm("")
    setDone(true)
  }

  return (
    <form onSubmit={change}>
      <SettingsSection
        title="Password"
        description="Changing it signs you out everywhere else."
      >
        <div className="flex flex-col gap-5">
          <SettingsRow label="Current password" htmlFor="pw-current">
            <Input
              id="pw-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </SettingsRow>
          <SettingsRow
            label="New password"
            htmlFor="pw-new"
            description={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          >
            <Input
              id="pw-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </SettingsRow>
          <SettingsRow label="Repeat it" htmlFor="pw-confirm">
            <Input
              id="pw-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </SettingsRow>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              disabled={working || current === "" || next === ""}
            >
              {working && <Spinner />}
              Change password
            </Button>
            {error && (
              <span role="alert" className="text-sm text-destructive">
                {error}
              </span>
            )}
            {done && (
              <span
                role="status"
                className="text-sm text-emerald-600 dark:text-emerald-400"
              >
                Password changed.
              </span>
            )}
          </div>
        </div>
      </SettingsSection>
    </form>
  )
}

export function ProfilePage() {
  const { data: session } = useSession()
  // A password only exists if one was set; people who sign in by email link have none to change.
  const [hasPassword, setHasPassword] = useState<boolean | null>(null)

  useEffect(() => {
    let active = true

    void authClient.listAccounts().then(({ data }) => {
      if (active) {
        setHasPassword(
          (data ?? []).some((account) => account.providerId === "credential")
        )
      }
    })

    return () => {
      active = false
    }
  }, [])

  if (!session) return null

  const { user } = session

  return (
    <div className="flex max-w-3xl flex-col gap-10">
      <PageHeader
        title="Profile"
        description="Your account for this dashboard."
        actions={
          <Button variant="outline" onClick={() => void authClient.signOut()}>
            <LogOut />
            Sign out
          </Button>
        }
      />

      <NameSection name={user.name ?? ""} />

      <SettingsSection
        title="Sign-in"
        description="How you get into the dashboard."
      >
        <div className="flex flex-col gap-5">
          <SettingsRow
            label="Email"
            description="Sign-in links and password resets go here."
          >
            <p className="text-sm">{user.email}</p>
          </SettingsRow>
          <SettingsRow label="Role">
            <p className="text-sm capitalize">{user.role ?? "admin"}</p>
          </SettingsRow>
        </div>
      </SettingsSection>

      {hasPassword === true && <PasswordSection />}
      {hasPassword === false && (
        <SettingsSection
          title="Password"
          description="You sign in with a link sent to your email, so there is no password to change."
        >
          <p className="text-sm text-muted-foreground">
            A password can be set from the sign-in page with “Forgot password”.
          </p>
        </SettingsSection>
      )}
    </div>
  )
}
